import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  Package, 
  DollarSign, 
  BarChart3, 
  PieChart,
  RefreshCw,
  AlertCircle,
  Clock,
  ShoppingCart,
  Building2,
  Zap,
  Archive,
  CheckCircle,
  ArrowLeft,
  X
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

const Analytics = ({ user, onBackToDashboard }) => {
  const [activeTab, setActiveTab] = useState('alerts');
  const [loading, setLoading] = useState(false);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [lowStockAlerts, setLowStockAlerts] = useState(null);
  const [expiryAlerts, setExpiryAlerts] = useState(null);
  const [reorderSuggestions, setReorderSuggestions] = useState(null);
  const [salesOverview, setSalesOverview] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [locationComparison, setLocationComparison] = useState(null);
  const [stockMovement, setStockMovement] = useState(null);
  const [categoryAnalytics, setCategoryAnalytics] = useState(null);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  
  // Modal state for clickable cards
  const [detailModal, setDetailModal] = useState({ open: false, type: '', title: '', data: [] });

  const fetchAlertsSummary = async () => {
    try {
      const response = await axios.get(`${API}/alerts/summary`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setAlertsSummary(response.data);
    } catch (error) {
      console.error('Error fetching alerts summary:', error);
    }
  };

  const fetchLowStockAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts/low-stock?threshold=100`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setLowStockAlerts(response.data);
    } catch (error) {
      toast.error('Failed to fetch low stock alerts');
    }
  };

  const fetchExpiryAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts/expiring-stock?days=90`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setExpiryAlerts(response.data);
    } catch (error) {
      toast.error('Failed to fetch expiry alerts');
    }
  };

  const fetchReorderSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/alerts/reorder-suggestions?months_coverage=3`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setReorderSuggestions(response.data);
    } catch (error) {
      toast.error('Failed to fetch reorder suggestions');
    }
  };

  const fetchSalesOverview = async () => {
    try {
      const response = await axios.get(`${API}/analytics/sales-overview`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setSalesOverview(response.data);
    } catch (error) {
      toast.error('Failed to fetch sales overview');
    }
  };

  const fetchTopProducts = async (sortBy = 'sale') => {
    try {
      const response = await axios.get(`${API}/analytics/top-products?limit=10&sort_by=${sortBy}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setTopProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch top products');
    }
  };

  const fetchLocationComparison = async () => {
    try {
      const response = await axios.get(`${API}/analytics/location-comparison`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setLocationComparison(response.data);
    } catch (error) {
      toast.error('Failed to fetch location comparison');
    }
  };

  const fetchStockMovement = async () => {
    try {
      const response = await axios.get(`${API}/analytics/stock-movement`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setStockMovement(response.data);
    } catch (error) {
      toast.error('Failed to fetch stock movement');
    }
  };

  const fetchCategoryAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/category-wise`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setCategoryAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch category analytics');
    }
  };

  const fetchCompletedOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/completed`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setCompletedOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch completed orders:', error);
    }
  };

  const fetchActiveOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      // Filter to only active orders (no received date)
      const active = response.data.filter(o => !o.order_received_date);
      setActiveOrders(active);
    } catch (error) {
      console.error('Failed to fetch active orders:', error);
    }
  };

  // Helper to check if a product has an active order
  const getActiveOrderForProduct = (productName) => {
    return activeOrders.find(order => 
      order.product_name?.toLowerCase() === productName?.toLowerCase()
    );
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchAlertsSummary(),
      fetchLowStockAlerts(),
      fetchExpiryAlerts(),
      fetchReorderSuggestions(),
      fetchSalesOverview(),
      fetchTopProducts(),
      fetchLocationComparison(),
      fetchStockMovement(),
      fetchCategoryAnalytics(),
      fetchCompletedOrders(),
      fetchActiveOrders()
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  // Handle card clicks to show detail modal
  const handleCardClick = (type, title) => {
    let data = [];
    switch(type) {
      case 'low_stock':
        data = lowStockAlerts?.alerts || [];
        break;
      case 'expiring':
        data = expiryAlerts?.alerts || [];
        break;
      case 'reorder':
        data = reorderSuggestions?.suggestions || [];
        break;
      case 'total_products':
        // Show all products summary
        data = lowStockAlerts?.alerts || [];
        break;
      case 'monthly_sales':
        data = topProducts?.products || [];
        break;
      case 'stock_value':
        data = [
          { location: 'SRH', value: locationComparison?.srh?.total_value || 0, qty: locationComparison?.srh?.total_qty || 0 },
          { location: 'WINLIFE', value: locationComparison?.winlife?.total_value || 0, qty: locationComparison?.winlife?.total_qty || 0 }
        ];
        break;
      case 'orders_completed':
        data = completedOrders || [];
        break;
      case 'fast_moving':
        data = stockMovement?.fast_moving?.products || [];
        break;
      case 'healthy':
        data = stockMovement?.healthy?.products || [];
        break;
      case 'slow_moving':
        data = stockMovement?.slow_moving?.products || [];
        break;
      case 'dead_stock':
        data = stockMovement?.dead_stock?.products || [];
        break;
      default:
        data = [];
    }
    setDetailModal({ open: true, type, title, data });
  };

  // Render detail modal
  const renderDetailModal = () => {
    if (!detailModal.open) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">{detailModal.title}</h3>
            <Button 
              onClick={() => setDetailModal({ open: false, type: '', title: '', data: [] })}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {detailModal.data.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No data available</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    {detailModal.type === 'stock_value' ? (
                      <>
                        <th className="px-4 py-3 text-left">Location</th>
                        <th className="px-4 py-3 text-right">Total Qty</th>
                        <th className="px-4 py-3 text-right">Total Value</th>
                      </>
                    ) : detailModal.type === 'orders_completed' ? (
                      <>
                        <th className="px-4 py-3 text-left">Product Name</th>
                        <th className="px-4 py-3 text-left">Order Given To</th>
                        <th className="px-4 py-3 text-right">Order Qty</th>
                        <th className="px-4 py-3 text-left">Batch No</th>
                        <th className="px-4 py-3 text-left">Expiry Date</th>
                        <th className="px-4 py-3 text-left">Received Date</th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-3 text-left">Product Name</th>
                        <th className="px-4 py-3 text-right">Stock</th>
                        <th className="px-4 py-3 text-right">Monthly Sale</th>
                        {detailModal.type === 'expiring' && <th className="px-4 py-3 text-left">Expiry Date</th>}
                        {detailModal.type === 'reorder' && <th className="px-4 py-3 text-right text-green-700">Suggested Order</th>}
                        <th className="px-4 py-3 text-center">Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {detailModal.data.map((item, idx) => (
                    <tr key={idx} className="border-t hover:bg-slate-50">
                      {detailModal.type === 'stock_value' ? (
                        <>
                          <td className="px-4 py-3 font-medium">{item.location}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(item.qty)}</td>
                          <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(item.value)}</td>
                        </>
                      ) : detailModal.type === 'orders_completed' ? (
                        <>
                          <td className="px-4 py-3 font-medium">{item.product_name}</td>
                          <td className="px-4 py-3">{item.order_given_to || '-'}</td>
                          <td className="px-4 py-3 text-right font-bold text-indigo-600">{formatNumber(item.order_qty)}</td>
                          <td className="px-4 py-3">{item.batch_no || '-'}</td>
                          <td className="px-4 py-3">{item.expiry_date || '-'}</td>
                          <td className="px-4 py-3">{item.order_received_date || '-'}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 font-medium">{item.product_name}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(item.current_stock || item.stock || 0)}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(item.monthly_sale || item.sale_qty_last_month || 0)}</td>
                          {detailModal.type === 'expiring' && <td className="px-4 py-3">{item.expiry_date}</td>}
                          {detailModal.type === 'reorder' && <td className="px-4 py-3 text-right font-bold text-green-700">{formatNumber(item.suggested_order_qty)}</td>}
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(item.priority || 'low')}`}>
                              {item.priority || 'normal'}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAlertsTab = () => (
    <div className="space-y-6">
      {/* Alerts Summary Cards - Clickable */}
      {alertsSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className="bg-gradient-to-br from-red-500 to-red-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => handleCardClick('low_stock', 'Low Stock Products')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">Low Stock Alerts</p>
                  <p className="text-3xl font-bold">{alertsSummary.low_stock.count}</p>
                  <p className="text-red-200 text-xs">{alertsSummary.low_stock.critical} critical • Click to view</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => handleCardClick('expiring', 'Expiring Stock')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Expiring Soon</p>
                  <p className="text-3xl font-bold">{alertsSummary.expiry.expiring_soon}</p>
                  <p className="text-orange-200 text-xs">{alertsSummary.expiry.expired} expired • Click to view</p>
                </div>
                <Clock className="w-10 h-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => handleCardClick('reorder', 'Reorder Suggestions')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Reorder Needed</p>
                  <p className="text-3xl font-bold">{alertsSummary.reorder_needed}</p>
                  <p className="text-blue-200 text-xs">Below 3 months • Click to view</p>
                </div>
                <ShoppingCart className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => handleCardClick('total_products', 'All Products')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Total Products</p>
                  <p className="text-3xl font-bold">{alertsSummary.total_products}</p>
                  <p className="text-green-200 text-xs">In inventory • Click to view</p>
                </div>
                <Package className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Low Stock Alerts */}
      {lowStockAlerts && lowStockAlerts.alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Low Stock Alerts ({lowStockAlerts.total_alerts})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Stock</th>
                    <th className="px-3 py-2 text-right">Monthly Sale</th>
                    <th className="px-3 py-2 text-right">Months Left</th>
                    <th className="px-3 py-2 text-center">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockAlerts.alerts.slice(0, 10).map((alert, idx) => (
                    <tr key={idx} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{alert.product_name}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(alert.current_stock)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(alert.monthly_sale)}</td>
                      <td className="px-3 py-2 text-right">{alert.months_remaining}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(alert.priority)}`}>
                          {alert.priority}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reorder Suggestions */}
      {reorderSuggestions && reorderSuggestions.suggestions.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShoppingCart className="w-5 h-5 text-blue-500" />
              Auto-Reorder Suggestions ({reorderSuggestions.total_suggestions})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Current Stock</th>
                    <th className="px-3 py-2 text-right">Monthly Sale</th>
                    <th className="px-3 py-2 text-right">Months Left</th>
                    <th className="px-3 py-2 text-right text-green-700">Suggested Order</th>
                    <th className="px-3 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reorderSuggestions.suggestions.slice(0, 10).map((suggestion, idx) => {
                    const activeOrder = getActiveOrderForProduct(suggestion.product_name);
                    const hasActiveOrder = !!activeOrder;
                    
                    return (
                      <tr 
                        key={idx} 
                        className={`border-t hover:bg-slate-50 ${hasActiveOrder ? 'bg-green-50/50' : ''}`}
                        title={hasActiveOrder ? `Order placed: ${activeOrder.order_qty?.toLocaleString()} units to ${activeOrder.order_given_to || 'N/A'}` : ''}
                      >
                        <td className={`px-3 py-2 font-medium ${hasActiveOrder ? 'line-through text-slate-400' : ''}`}>
                          {suggestion.product_name}
                          {hasActiveOrder && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 no-underline" style={{textDecoration: 'none'}}>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Order Placed
                            </span>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right ${hasActiveOrder ? 'line-through text-slate-400' : ''}`}>{formatNumber(suggestion.current_stock)}</td>
                        <td className={`px-3 py-2 text-right ${hasActiveOrder ? 'line-through text-slate-400' : ''}`}>{formatNumber(suggestion.avg_monthly_sale)}</td>
                        <td className={`px-3 py-2 text-right ${hasActiveOrder ? 'line-through text-slate-400' : ''}`}>{suggestion.months_of_stock}</td>
                        <td className={`px-3 py-2 text-right font-bold ${hasActiveOrder ? 'line-through text-slate-400' : 'text-green-700'}`}>{formatNumber(suggestion.suggested_order_qty)}</td>
                        <td className="px-3 py-2 text-center">
                          {hasActiveOrder ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 cursor-help"
                              title={`Qty: ${activeOrder.order_qty?.toLocaleString()} | To: ${activeOrder.order_given_to || 'N/A'} | Date: ${activeOrder.order_given_date || 'N/A'}`}
                            >
                              ✓ Ordered
                            </span>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(suggestion.priority)}`}>
                              {suggestion.priority}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiry Alerts */}
      {expiryAlerts && expiryAlerts.alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-orange-500" />
              Expiring Stock Alerts ({expiryAlerts.total_alerts})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-left">Expiry Date</th>
                    <th className="px-3 py-2 text-right">Days Left</th>
                    <th className="px-3 py-2 text-right">Stock</th>
                    <th className="px-3 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {expiryAlerts.alerts.slice(0, 10).map((alert, idx) => (
                    <tr key={idx} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{alert.product_name}</td>
                      <td className="px-3 py-2">{alert.expiry_date}</td>
                      <td className="px-3 py-2 text-right">{alert.days_until_expiry}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(alert.current_stock)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(alert.priority)}`}>
                          {alert.priority}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderSalesTab = () => (
    <div className="space-y-6">
      {/* Sales Overview Cards - Clickable */}
      {salesOverview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => handleCardClick('monthly_sales', 'Top Products by Sales')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">Monthly Sales Qty</p>
                  <p className="text-3xl font-bold">{formatNumber(salesOverview.sales.monthly_sale_qty)}</p>
                  <p className="text-indigo-200 text-xs">Units sold • Click to view</p>
                </div>
                <TrendingUp className="w-10 h-10 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => handleCardClick('stock_value', 'Stock Value by Location')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total Stock Value</p>
                  <p className="text-3xl font-bold">{formatCurrency(salesOverview.stock_value.total)}</p>
                  <p className="text-emerald-200 text-xs">SRH + WINLIFE • Click to view</p>
                </div>
                <DollarSign className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => handleCardClick('orders_completed', 'Completed Orders')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Orders Completed</p>
                  <p className="text-3xl font-bold">{salesOverview.orders.completed_count}</p>
                  <p className="text-purple-200 text-xs">{formatNumber(salesOverview.orders.total_qty_ordered)} units ordered</p>
                </div>
                <CheckCircle className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Location Comparison Chart */}
      {locationComparison && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-indigo-500" />
              Location Comparison (SRH vs WINLIFE)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'SRH', value: locationComparison.srh.total_value, qty: locationComparison.srh.total_qty },
                    { name: 'WINLIFE', value: locationComparison.winlife.total_value, qty: locationComparison.winlife.total_qty }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="value" name="Stock Value" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <p className="text-sm text-indigo-600 font-medium">SRH Location</p>
                  <p className="text-2xl font-bold text-indigo-900">{formatCurrency(locationComparison.srh.total_value)}</p>
                  <p className="text-sm text-indigo-700">{locationComparison.srh.products_with_stock} products • {formatNumber(locationComparison.srh.total_qty)} units</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-600 font-medium">WINLIFE Location</p>
                  <p className="text-2xl font-bold text-emerald-900">{formatCurrency(locationComparison.winlife.total_value)}</p>
                  <p className="text-sm text-emerald-700">{locationComparison.winlife.products_with_stock} products • {formatNumber(locationComparison.winlife.total_qty)} units</p>
                </div>
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-sm text-slate-600 font-medium">Distribution</p>
                  <p className="text-lg font-bold text-slate-900">SRH: {locationComparison.comparison.srh_percentage}% • WINLIFE: {(100 - locationComparison.comparison.srh_percentage).toFixed(1)}%</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Products */}
      {topProducts && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Top 10 Products by Sales
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant={topProducts.sort_by === 'sale' ? 'default' : 'outline'} onClick={() => fetchTopProducts('sale')}>
                  By Sale
                </Button>
                <Button size="sm" variant={topProducts.sort_by === 'value' ? 'default' : 'outline'} onClick={() => fetchTopProducts('value')}>
                  By Value
                </Button>
                <Button size="sm" variant={topProducts.sort_by === 'stock' ? 'default' : 'outline'} onClick={() => fetchTopProducts('stock')}>
                  By Stock
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topProducts.products.slice(0, 5).map(p => ({
                    name: p.product_name.length > 15 ? p.product_name.substring(0, 15) + '...' : p.product_name,
                    sales: p.sale_qty_last_month,
                    stock: p.total_stock
                  }))} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sales" name="Monthly Sales" fill="#22c55e" />
                    <Bar dataKey="stock" name="Total Stock" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Product</th>
                      <th className="px-2 py-2 text-right">Sales</th>
                      <th className="px-2 py-2 text-right">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.products.map((product, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="px-2 py-2 font-medium text-indigo-600">{idx + 1}</td>
                        <td className="px-2 py-2 text-xs">{product.product_name}</td>
                        <td className="px-2 py-2 text-right text-green-600 font-medium">{formatNumber(product.sale_qty_last_month)}</td>
                        <td className="px-2 py-2 text-right">{formatNumber(product.total_stock)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stock Movement Analysis - Clickable Cards */}
      {stockMovement && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-yellow-500" />
              Stock Movement Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div 
                className="p-4 bg-green-50 rounded-lg border border-green-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                onClick={() => handleCardClick('fast_moving', 'Fast Moving Products')}
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Fast Moving</span>
                </div>
                <p className="text-2xl font-bold text-green-900 mt-2">{stockMovement.fast_moving.count}</p>
                <p className="text-xs text-green-600">{stockMovement.fast_moving.description}</p>
                <p className="text-xs text-green-500 mt-1">Click to view</p>
              </div>
              <div 
                className="p-4 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                onClick={() => handleCardClick('healthy', 'Healthy Stock Products')}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">Healthy</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 mt-2">{stockMovement.healthy.count}</p>
                <p className="text-xs text-blue-600">{stockMovement.healthy.description}</p>
                <p className="text-xs text-blue-500 mt-1">Click to view</p>
              </div>
              <div 
                className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                onClick={() => handleCardClick('slow_moving', 'Slow Moving Products')}
              >
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-yellow-600" />
                  <span className="font-medium text-yellow-800">Slow Moving</span>
                </div>
                <p className="text-2xl font-bold text-yellow-900 mt-2">{stockMovement.slow_moving.count}</p>
                <p className="text-xs text-yellow-600">{stockMovement.slow_moving.description}</p>
                <p className="text-xs text-yellow-500 mt-1">Click to view</p>
              </div>
              <div 
                className="p-4 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all"
                onClick={() => handleCardClick('dead_stock', 'Dead Stock Products')}
              >
                <div className="flex items-center gap-2">
                  <Archive className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-800">Dead Stock</span>
                </div>
                <p className="text-2xl font-bold text-red-900 mt-2">{stockMovement.dead_stock.count}</p>
                <p className="text-xs text-red-600">{stockMovement.dead_stock.description}</p>
                <p className="text-xs text-red-500 mt-1">Click to view</p>
              </div>
            </div>
            
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={[
                      { name: 'Fast Moving', value: stockMovement.fast_moving.count, color: '#22c55e' },
                      { name: 'Healthy', value: stockMovement.healthy.count, color: '#3b82f6' },
                      { name: 'Slow Moving', value: stockMovement.slow_moving.count, color: '#eab308' },
                      { name: 'Dead Stock', value: stockMovement.dead_stock.count, color: '#ef4444' }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      { name: 'Fast Moving', value: stockMovement.fast_moving.count, color: '#22c55e' },
                      { name: 'Healthy', value: stockMovement.healthy.count, color: '#3b82f6' },
                      { name: 'Slow Moving', value: stockMovement.slow_moving.count, color: '#eab308' },
                      { name: 'Dead Stock', value: stockMovement.dead_stock.count, color: '#ef4444' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category/Manufacturer Analytics */}
      {categoryAnalytics && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart className="w-5 h-5 text-purple-500" />
              Manufacturer-wise Analysis ({categoryAnalytics.total_manufacturers})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Manufacturer</th>
                    <th className="px-3 py-2 text-right">Products</th>
                    <th className="px-3 py-2 text-right">Total Stock</th>
                    <th className="px-3 py-2 text-right">Stock Value</th>
                    <th className="px-3 py-2 text-right">Monthly Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryAnalytics.categories.slice(0, 10).map((cat, idx) => (
                    <tr key={idx} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{cat.manufacturer || 'Unknown'}</td>
                      <td className="px-3 py-2 text-right">{cat.product_count}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(cat.total_stock)}</td>
                      <td className="px-3 py-2 text-right font-medium text-green-600">{formatCurrency(cat.total_value)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(cat.total_sales)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Detail Modal */}
      {renderDetailModal()}

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Analytics & Alerts</h2>
            <p className="text-indigo-100 text-sm sm:text-base">Inventory alerts, sales analytics & business insights</p>
          </div>
          <div className="flex gap-2">
            {onBackToDashboard && (
              <Button 
                onClick={onBackToDashboard}
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
            <Button 
              onClick={fetchAllData} 
              disabled={loading}
              className="bg-white text-indigo-600 hover:bg-indigo-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Fixed Colors */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-lg w-fit">
        <Button
          onClick={() => setActiveTab('alerts')}
          variant={activeTab === 'alerts' ? 'default' : 'ghost'}
          className={activeTab === 'alerts' 
            ? 'bg-white shadow-sm text-slate-900' 
            : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          Inventory Alerts
        </Button>
        <Button
          onClick={() => setActiveTab('sales')}
          variant={activeTab === 'sales' ? 'default' : 'ghost'}
          className={activeTab === 'sales' 
            ? 'bg-white shadow-sm text-slate-900' 
            : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Sales Analytics
        </Button>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
          <span className="ml-3 text-slate-600">Loading analytics...</span>
        </div>
      ) : (
        <>
          {activeTab === 'alerts' && renderAlertsTab()}
          {activeTab === 'sales' && renderSalesTab()}
        </>
      )}
    </div>
  );
};

export default Analytics;
